import {useTranslation} from 'react-i18next';
import './occurrences.professional-caregiver.screen.scss';
import {useMemo, useState} from 'react';
import {
  Box,
  Paper,
  Skeleton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  tableCellClasses,
} from '@mui/material';
import {BsCheckAll} from 'react-icons/bs';
import moment from 'moment';
import {translations} from '../../../../../i18n/translations';
import {tableSx} from '../../../../../theme/table-sx';
import {TextCell} from '../../../../../components/task-table/cells';
import {UserCell} from './user';
import {SortIcon} from '../../../../../components/task-table/sort-icon';
import {OccurrenceStatus} from './occurrences-status';
import {useAuth} from '../../../../../contexts/auth/context';
import {TransformedOccurrence} from '../../../../../utils/data/occurrences';

type ColumnsId =
  | 'date'
  | 'type'
  | 'patient'
  | 'responsible'
  | 'registered'
  | 'status'
  | 'acknowledge'
  | 'buttons';

type OccurrenceDataProps = {
  data?: TransformedOccurrence[];
  isLoading: boolean;
  onSelect: (id: string) => void;
};
type SortDirection = 'asc' | 'desc';

export default function OccurrencesTable({data, isLoading, onSelect}: OccurrenceDataProps) {
  const {t} = useTranslation();
  const {state: authState} = useAuth();
  const [order, setOrder] = useState<SortDirection>('desc');
  const [orderBy, setOrderBy] = useState<ColumnsId>('date');
  let columns: Record<
    ColumnsId,
    {
      label: string;
      sortable: boolean;
      sx?: SxProps<Theme>;
    }
  > = {
    date: {
      label: t(translations.office.professional_caregiver.occurrences.table_header_date),
      sortable: true,
    },
    type: {
      label: t(translations.office.professional_caregiver.occurrences.table_header_type),
      sortable: true,
    },
    patient: {
      label: t(translations.office.professional_caregiver.occurrences.table_header_patient),
      sortable: true,
    },
    responsible: {
      label: t(translations.office.professional_caregiver.occurrences.table_header_responsible),
      sortable: true,
    },
    registered: {
      label: t(translations.office.professional_caregiver.occurrences.table_header_registered),
      sortable: true,
    },
    status: {
      label: t(translations.office.professional_caregiver.occurrences.table_header_status),
      sortable: true,
    },
    acknowledge: {
      label: t(translations.office.professional_caregiver.occurrences.table_header_knowledge),
      sortable: false,
    },
    buttons: {
      label: '',
      sortable: false,
    },
  };

  const mappedData = useMemo(() => {
    if (!data) return [];
    let prop: 'registeredAt' | 'patient' | 'responsible' | 'status' | 'type' = 'registeredAt';

    return [...data].sort((a, b) => {
      if (orderBy === 'date') prop = 'registeredAt';
      else if (orderBy === 'patient') prop = 'patient';
      else if (orderBy === 'responsible') prop = 'responsible';
      else if (orderBy === 'status') prop = 'status';
      else if (orderBy === 'type') prop = 'type';

      if (prop === 'patient' || prop === 'responsible') {
        const objectPropA = a[prop as 'patient' | 'responsible'];
        const objectPropB = b[prop as 'patient' | 'responsible'];
        if (objectPropA === undefined || objectPropB === undefined) {
          return 0;
        }
        if (objectPropA.name < objectPropB.name) {
          return order === 'asc' ? -1 : 1;
        } else if (objectPropA.name > objectPropB.name) {
          return order === 'asc' ? 1 : -1;
        }
        return 0;
      }

      if (a[prop] < b[prop]) {
        return order === 'asc' ? -1 : 1;
      } else if (a[prop] > b[prop]) {
        return order === 'asc' ? 1 : -1;
      }

      return 0;
    });
  }, [data, order, orderBy]);

  const handleColumnSort = (columnId: ColumnsId) => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
  };

  return (
    <TableContainer component={Paper} sx={{...tableSx.container, marginTop: '0'}}>
      <Table stickyHeader sx={tableSx.table}>
        <TableHead>
          <TableRow
            sx={{
              [`& .${tableCellClasses.root}`]: {
                ...tableSx.header[`& .${tableCellClasses.root}`],
                fontWeight: '500',
              },
            }}
          >
            {Object.entries(columns).map(([key, {label, sortable, sx}], index) => {
              const columnId = key as ColumnsId;
              const active = orderBy === columnId;
              const sortDirection = active ? order : 'asc';

              return (
                <TableCell key={`cell_for_${columnId}_${index}`} sx={sx}>
                  {sortable ? (
                    <TableSortLabel
                      active={active}
                      direction={sortDirection}
                      onClick={() => handleColumnSort(columnId)}
                      IconComponent={() => <SortIcon active={active} direction={sortDirection} />}
                      sx={{
                        fontWeight: 500,
                        '&.Mui-active': {
                          color: 'var(--dcm-gray)',
                        },
                      }}
                    >
                      {label}
                    </TableSortLabel>
                  ) : (
                    <span className="care-table-header-cell-label">{label}</span>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading || !data
            ? new Array(6).fill(1).map((_item, index) => (
                <TableRow key={`skeleton_${index}`}>
                  <TableCell colSpan={7} sx={{padding: '0 10px'}}>
                    <Skeleton animation="wave" sx={{transform: 'scale(1, 0.78)'}} height={40} />
                  </TableCell>
                </TableRow>
              ))
            : mappedData.map((item) => {
                return (
                  <TableRow
                    key={`table_row_${item.id}`}
                    className="occurrences-table-row"
                    sx={{
                      ...tableSx.row,
                      backgroundColor:
                        item.status === 'not_handled'
                          ? 'var(--dcm-yellow-missed)'
                          : item.status === 'handled'
                            ? 'var(--dcm-green-handled)'
                            : undefined,
                      borderRadius: undefined,
                      '& .MuiTableCell-root': {
                        ...tableSx.row['& .MuiTableCell-root'],
                        height: 'auto',
                      },
                    }}
                    onClick={() => onSelect(item.id)}
                  >
                    <TableCell className="date">
                      <TextCell value={moment(item.registeredAt).format('DD/MM/YYYY HH:mm')} />
                    </TableCell>
                    <TableCell className="type" title={item.type}>
                      <TextCell value={item.type} />
                    </TableCell>
                    <TableCell className="patient">
                      <UserCell
                        name={item.patient?.name || ''}
                        picture={item.patient?.picture}
                        borderColor={
                          item.patient && item.patient.socialResponses.length > 0
                            ? item.patient.socialResponses[0].foregroundColor
                            : ''
                        }
                      />
                    </TableCell>
                    <TableCell className="responsible">
                      <UserCell
                        name={item.responsible?.name || ''}
                        picture={item.responsible?.picture}
                      />
                    </TableCell>
                    <TableCell className="registered">
                      <UserCell
                        name={item.registeredBy?.name || ''}
                        picture={item.registeredBy?.picture}
                      />
                    </TableCell>
                    <TableCell className="status">
                      <OccurrenceStatus status={item.status} />
                    </TableCell>
                    <TableCell className="status">
                      {item.acknowledgements.find(
                        (ack) =>
                          ack.userAuthorId === authState.session?.currentUser.id &&
                          ack.acknowledgedAt
                      ) ? (
                        <Box
                          sx={{
                            '> svg': {
                              width: '20px',
                              height: '20px',
                            },
                          }}
                        >
                          <BsCheckAll />
                        </Box>
                      ) : (
                        <></>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
